// This file contains logic around checking whether or not media file paths actually exist.
// It's wrapped with a custom caching/deduping layer (see `./cache.ts`).
// Very similar to `./directURL.ts`.

import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';

import {
  type AvailableFilesQueryQuery,
  type AvailableFilesQueryQueryVariables,
} from '../../../../generated/graphql';
import {AVAILABLE_FILES_QUERY} from '../../../../graphql/media';
import {type RunSignature} from '../../../../types/run';
import {type Thumbnails} from '../../../MediaCard';
import {getKeyForFilePath} from '../keys';
import {fetchGQL} from '../util';
import {type BaseFetchParams} from '.';
import {fetchWithCache} from './cache';

type FilePathExistence = boolean | null;

const filePathExistencePromiseByFilePathKey: Map<
  string,
  Promise<FilePathExistence>
> = new Map();

/**
 * URL data extracted from AVAILABLE_FILES_QUERY (existence + signed URLs
 * in one trip). Stored here so fetch.ts can build URL maps without issuing
 * a second DirectUrlQuery.
 */
export type UnifiedUrlData = {
  directUrl: string | null | undefined;
  internalUrl: string;
  thumbnails: Thumbnails | null | undefined;
  hlsSrc: string | null | undefined;
};

const unifiedUrlDataByFilePathKey: Map<string, UnifiedUrlData> = new Map();

export function getUnifiedUrlDataForFilePath(
  runSignature: RunSignature,
  filePath: string
): UnifiedUrlData | undefined {
  return unifiedUrlDataByFilePathKey.get(
    getKeyForFilePath(runSignature, filePath)
  );
}

/**
 * Clear AVAILABLE_FILES client caches for a run+file so the next fetch re-queries
 * GraphQL and repopulates URL data (e.g. after signed URL expiry).
 */
export function invalidateAvailableFilesCacheForFilePath(
  runSignature: RunSignature,
  filePath: string
): void {
  const key = getKeyForFilePath(runSignature, filePath);
  unifiedUrlDataByFilePathKey.delete(key);
  filePathExistencePromiseByFilePathKey.delete(key);
}

export async function fetchExistingFilePathSet({
  runSignature,
  filePaths,
}: BaseFetchParams): ReturnType<typeof fetchExistingFilePathSetUncached> {
  return fetchWithCache({
    runSignature,
    filePaths,
    pathPromiseByKey: filePathExistencePromiseByFilePathKey,
    fetchNew: fetchExistingFilePathSetUncached,
    getPromiseForPath: getExistencePromiseForPath,
    getReturnValue: makeExistingFilePathSet,
  });
}

async function fetchExistingFilePathSetUncached({
  runSignature,
  filePaths,
}: BaseFetchParams): Promise<Set<string> | null> {
  if (filePaths.length === 0) {
    return new Set();
  }

  const data = await fetchGQL<
    AvailableFilesQueryQueryVariables,
    AvailableFilesQueryQuery
  >({
    query: AVAILABLE_FILES_QUERY,
    variables: {
      ...runSignature,
      filenames: filePaths,
      includeDirectUrl: true,
      includeThumbnails: true,
      includeHlsSrc: true,
    },
  });

  const existingNodes =
    data?.project?.run?.files?.edges
      ?.map(e => e.node)
      .filter(isNotNullOrUndefined)
      .filter(f => f.sizeBytes > 0) ?? [];

  for (const node of existingNodes) {
    unifiedUrlDataByFilePathKey.set(
      getKeyForFilePath(runSignature, node.name),
      {
        directUrl: node.directUrl,
        internalUrl: node.url || '',
        thumbnails: node.thumbnails,
        hlsSrc: node.hlsSrc,
      }
    );
  }

  return new Set(existingNodes.map(f => f.name));
}

async function getExistencePromiseForPath(
  existingSetPromise: ReturnType<typeof fetchExistingFilePathSetUncached>,
  filePath: string
): Promise<FilePathExistence> {
  const existingSet = await existingSetPromise;
  if (existingSet == null) {
    return null;
  }
  return existingSet.has(filePath);
}

function makeExistingFilePathSet(
  filePaths: string[],
  filePathExistences: FilePathExistence[]
): Set<string> {
  return new Set(filePaths.filter((filePath, i) => filePathExistences[i]));
}
